import { type TradeInAemSearchType, TradeInAemNavStep } from "../../../utils/constants";
import { TradeInCsl } from "../../TradeInCsl";
import {
    TradeInAemResolveItemType,
    type DateRangeMap,
    type TradeInAemEHubPayload,
    type TradeInAemExtraVehicleDetailsPdfBaseType,
    type TradeInAemFetchQuoteFunctionReturnType,
    type TradeInAemFetchVehicleResultsReturnType,
    type TradeInAemResolveItemConfig,
    type TradeInAemVehicleExtraInfoType,
    type TradeInAemVehicleNameType,
    type TradeInAemVehicleType,
    type TradeInAemVehicleValueType,
} from "../../../utils/types";
import {
    enablePdf,
    fetchFindVehicle,
    fetchVehicleValue,
    getParsedVehicleDetails,
    getTradeInAemPdfUrl,
    handleQuoteFetch,
    handleVehicleFetch,
} from "../../../utils/utils";
import type { TradeInAemStateType } from "../../../redux/store";
import { type TradeInAemLabelType, getTradeInAemLabel } from "../../../utils/labels";
import { formatDate, formatNumber } from "../../../../common-deprecated/Globalize";
import { tradeInAemAddResolveItem } from "../../../redux/actions/TradeInAemFindVehicleActions";
import type { DealerFinderSettingsType } from "../../../../common-deprecated/components/AemDealerFinder";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
export type TradeInAemVehicleTypeGr = {
    make: string;
    model: string;
    grade: string;
    body: string;
    name: string;
    doors: string;
    fuel: string;
    cc: string;
    hp: string;
    carID: string;
    gears: string;
    start: string;
    end: string;

    // Extra info property - used to save some data which will be used for the PDF later
    tmeExtraInfo?: TradeInAemVehicleExtraInfoType;
};

// This response contains lots of other data, only typing what is relevant at our side.
export type TradeInAemGrValueApiResponseType = TradeInAemVehicleTypeGr & {
    quote: {
        value: {
            min: number;
            rules: unknown[];
            detail: unknown[];
            expires: string;
        };
        id: string;
    };
};

// Specific properties which are only shown for THEL in the vehicle extra details in the PDF
export type TradeInAemExtraVehicleDetailsPdfGrSpecificType = {
    name: string;
    carID: string;
    start: string;
    end: string;
};

// Combination of base props + THEL specific props
type TradeInAemGrExtraVehicleDetailsPdfType = TradeInAemExtraVehicleDetailsPdfBaseType &
    TradeInAemExtraVehicleDetailsPdfGrSpecificType;

// ----------------------------------------------------------------------
// CSL
// ----------------------------------------------------------------------

export class TradeInCslGr extends TradeInCsl {
    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    enabledNavSteps = [
        TradeInAemNavStep.FindVehicle,
        TradeInAemNavStep.VehicleDetails,
        TradeInAemNavStep.ContactForm,
        TradeInAemNavStep.DealerFinder,
        TradeInAemNavStep.ModelSelect,
        TradeInAemNavStep.Summary,
    ];

    enableVin = false;

    enableLicensePlate = false;

    enableResolve = true;

    enablePreviouslySearchedVehicles = false;

    resolveKeyMap: Record<string, TradeInAemLabelType> = {
        make: "tradeInAemResolveMake",
        model: "tradeInAemResolveModel",
        grade: "tradeInAemResolveGrade",
        body: "tradeInAemResolveBody",
        name: "tradeInAemResolveName",
    };

    dealerFinderSettings: DealerFinderSettingsType = { showResultsOnPageLoad: false };

    // THEL temporary has a DOI solution where the following happens:
    // 1. Summary is shown without prices + mail is send without PDF and without prices
    // Once the customer confirms the email
    // 2. Mail is sent with PDF which includes the valuation
    hideValuationValueOnSummary = true;

    vehicleValueSelector(state: TradeInAemStateType): TradeInAemVehicleValueType | null {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemGrValueApiResponseType | null;
        return valuation?.quote
            ? {
                  value: valuation.quote.value.min,
                  validityDate: valuation.quote.value.expires,
                  quoteId: valuation.quote.id,
              }
            : null;
    }

    vehiclePlateOrVinSelector(): null {
        return null;
    }

    vehicleInfoBlockSelector(state: TradeInAemStateType): { label: string; value: string }[] {
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGr;
        if (!vehicle) return [];

        const { formItems } = state.tradeInAemVehicleDetails;
        // The extra form items will be parsed and shown
        const { mileage, ...extraFormItems } = formItems;
        const mileageValue = formatNumber(Number(mileage.value as string), state.commonSettings.culture.name);
        const mileageLabel = getTradeInAemLabel(state, "tradeInAemMileageFormat").replace("{value}", mileageValue);

        const result = [{ label: mileage.label, value: mileageLabel }, ...getParsedVehicleDetails(extraFormItems)];

        return result;
    }

    vehicleQuoteExpirySelector(state: TradeInAemStateType): string {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemGrValueApiResponseType | null;
        if (!valuation?.quote) return "";

        const date = formatDate(
            new Date(valuation.quote.value.expires),
            "d MMMM yyyy",
            state.commonSettings.culture.name,
        );
        return getTradeInAemLabel(state, "tradeInAemVehicleValueValidity").replace("{date}", date);
    }

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------
    getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType {
        const typedVehicle = vehicle as TradeInAemVehicleTypeGr;
        return {
            name: `${typedVehicle.make} ${typedVehicle.model}`,
            description: typedVehicle.name,
        };
    }

    getVehicleKey(vehicle: TradeInAemVehicleType): string {
        const typedVehicle = vehicle as TradeInAemVehicleTypeGr;
        return `${typedVehicle.make}-${typedVehicle.model}-${typedVehicle.name} || ""
        }`;
    }

    async fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void> {
        const { dispatch } = this.getStore();
        const { commonSettings, tradeInAemSettings } = this.getStore().getState();

        const fetchFn = async (): Promise<TradeInAemFetchVehicleResultsReturnType> => {
            const headers = this.getTradeInApiHeaders("GET");
            const response = await fetchFindVehicle(value, type, commonSettings, tradeInAemSettings, headers);
            if (response.status === 200) {
                const jsonResponse = (await response.json()) as unknown as TradeInAemVehicleTypeGr;

                return { results: [jsonResponse] };
            } else {
                return { results: [] };
            }
        };

        await handleVehicleFetch(dispatch, fetchFn);
    }

    async fetchVehicleQuote(): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemVehicle, commonSettings, tradeInAemSettings } = getState();
        const { vehicle } = tradeInAemVehicle;
        const fetchFn = async (): TradeInAemFetchQuoteFunctionReturnType => {
            const headers = this.getTradeInApiHeaders("POST");
            const response = await fetchVehicleValue(commonSettings, tradeInAemSettings, vehicle, headers);
            const jsonResponse = await response.json();
            if (response.status === 200) {
                if (!jsonResponse.quote?.error) {
                    return {
                        valuation: jsonResponse as unknown as TradeInAemGrValueApiResponseType,
                        latestResponse: jsonResponse,
                    };
                }
            }
            return { valuation: null, latestResponse: jsonResponse };
        };
        await handleQuoteFetch(dispatch, fetchFn);
    }

    getFormItemDropdownRange(): DateRangeMap {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGr;

        const startYear = parseInt(vehicle.start.substring(0, 4));
        const startMonth = parseInt(vehicle.start.substring(4, 6));
        const endYear = parseInt(vehicle.end.substring(0, 4));
        const endMonth = parseInt(vehicle.end.substring(4, 6));
        const result: DateRangeMap = {};

        for (let year = startYear; year <= endYear; year++) {
            const months: number[] = [];

            if (year === startYear) {
                for (let month = startMonth; month <= 12; month++) {
                    months.push(month);
                }
            } else if (year === endYear) {
                for (let month = 1; month <= endMonth; month++) {
                    months.push(month);
                }
            } else {
                for (let month = 1; month <= 12; month++) {
                    months.push(month);
                }
            }

            result[year.toString()] = months;
        }

        return result;
    }

    getEHubVehicleData(): TradeInAemEHubPayload["vehicle"] {
        const currentState = this.getStore().getState();
        const vehicleName = this.vehicleNameSelector(currentState);
        const { tradeInAemVehicle, tradeInAemVehicleDetails, commonSettings } = currentState;
        const { tradeInAemSettings } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGr;

        const quoteId = this.vehicleValueSelector(currentState)?.quoteId || null;
        const pdfLink =
            enablePdf(commonSettings, tradeInAemSettings) && quoteId
                ? getTradeInAemPdfUrl(commonSettings, quoteId)
                : null;

        return {
            quoteId,
            licensePlate: null,
            name: `${vehicleName.name} - ${vehicleName.description || ""}`,
            manufacturer: vehicle.make,
            model: vehicle.model,
            bodyType: vehicle.name,
            mileage: (tradeInAemVehicleDetails.formItems?.mileage?.value as string) || null,
            doors: vehicle.doors,
            fuelType: vehicle.fuel,
            engine: null,
            transmission: null,
            modelYear: vehicle.start.substring(0, 4),
            co2: null,
            vin: null,
            pdfLink,
            registrationDate: null,
        };
    }

    getExtraVehicleDetailsPdf = (): TradeInAemGrExtraVehicleDetailsPdfType => {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        // The vehicle on the PDF is fetched via the quoteId, which is propagated from the value api.
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemGrValueApiResponseType;

        const baseObject: TradeInAemExtraVehicleDetailsPdfBaseType = {
            manufacturer: vehicle.make,
            model: vehicle.model,
            grade: vehicle.grade,
            generation: null,
            engine: null,
            powerHp: vehicle.hp,
            powerPs: null,
            powerKw: null,
            body: vehicle.body,
            doors: vehicle.doors,
            fuel: vehicle.fuel,
            transmission: null,
            gears: vehicle.gears,
            colour: null,
            regDate: null,
            vin: null,
            seats: null,
            wheeldrive: null,
            cc: vehicle.cc,
            year: null,
        };

        // THEL specific
        const csObject: TradeInAemExtraVehicleDetailsPdfGrSpecificType = {
            name: vehicle.name,
            carID: vehicle.carID,
            start: vehicle.start,
            end: vehicle.end,
        };

        return { ...baseObject, ...csObject };
    };

    getResolveOptions = (parsedResponse: Record<string, any>): boolean => {
        const { dispatch } = this.getStore();
        let newItem = false;
        Object.keys(parsedResponse).forEach((key) => {
            if (typeof parsedResponse[key]?.select !== "undefined") {
                newItem = true;

                const dropdownItem: TradeInAemResolveItemConfig = {
                    type: TradeInAemResolveItemType.Select,
                    vehicleProperty: key,
                    labelKey: this.resolveKeyMap[key] || key,
                    options: parsedResponse[key].select.map((selectItem: string) => ({
                        label: selectItem,
                        id: selectItem,
                    })),
                    required: true, // TODO This can become something else depending on CSL
                    selectedOptionId: "",
                };
                dispatch(tradeInAemAddResolveItem(dropdownItem));
            }
        });
        return newItem;
    };

    prefillFormsWithTestData(): void {
        super.prefillFormsWithTestData();

        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeGr;

        // Prefill custom vehicle details values
        this.setFormItemValue("mileage", "100000");
        this.setFormItemValue("month", parseInt(`${vehicle.start.substring(4, 6)}`, 10).toString()); // start example 201201: need 01 as string without leading 0.
        this.setFormItemValue("year", `${vehicle.start.substring(0, 4)}`);
    }
}
